<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import CampaignsToolbar from 'dashboard/components-next/Campaigns/CampaignsToolbar.vue';
import SMSCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/SMSCampaign/SMSCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import SMSCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/SMSCampaignEmptyState.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
const [showSMSCampaignDialog, toggleSMSCampaignDialog] = useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);

const SMSCampaigns = computed(() => getters['campaigns/getSMSCampaigns'].value);

const hasNoSMSCampaigns = computed(
  () => SMSCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const refreshCampaigns = async () => {
  await store.dispatch('campaigns/get');
};

// Search, Audience, Inbox and Status Filters
const searchValue = ref('');
const selectedLabels = ref([]);
const selectedInboxes = ref([]);
const filterOperator = ref('and');
const selectedStatuses = ref([]);

const stripHtml = s => (s || '').toString().replace(/<[^>]*>/g, ' ');
const contains = (hay, needle) =>
  (hay || '').toLowerCase().includes((needle || '').toLowerCase());

const normalizeStatus = s => {
  if (typeof s === 'number') {
    switch (s) {
      case 0:
        return 'active';
      case 1:
        return 'completed';
      case 2:
        return 'error';
      case 3:
        return 'running';
      default:
        return 'active';
    }
  }
  if (typeof s === 'string') {
    const v = s.toLowerCase();
    if (['active', 'completed', 'error', 'running'].includes(v)) return v;
  }
  return 'active';
};

const filteredSMSCampaigns = computed(() => {
  const list = SMSCampaigns.value || [];
  const q = searchValue.value?.trim().toLowerCase();
  const selected = new Set(
    Array.isArray(selectedLabels.value)
      ? selectedLabels.value.map(labelId => String(labelId))
      : []
  );

  return list.filter(c => {
    const title = c.title || '';
    const message = stripHtml(c.message || '');
    const templateName = c.template_params?.name || '';
    const matchesSearch =
      !q ||
      contains(title, q) ||
      contains(message, q) ||
      contains(templateName, q);

    if (!matchesSearch) return false;

    const audienceIds = new Set(
      (c.audience || [])
        .filter(
          a => a && a.type === 'Label' && a.id !== null && a.id !== undefined
        )
        .map(a => String(a.id))
    );
    const hasLabelSelection = selected.size > 0;
    const labelMatch =
      !hasLabelSelection ||
      Array.from(selected).every(id => audienceIds.has(id));

    const inboxIds = Array.isArray(selectedInboxes.value)
      ? selectedInboxes.value.map(inboxId => String(inboxId))
      : [];
    const hasInboxSelection = inboxIds.length > 0;
    const inboxId = c.inbox?.id;
    const inboxMatch =
      !hasInboxSelection ||
      (inboxId != null && inboxIds.includes(String(inboxId)));

    // Status match (active/completed/error/running)
    const statuses = Array.isArray(selectedStatuses.value)
      ? selectedStatuses.value
      : [];
    const hasStatusSelection = statuses.length > 0;
    const statusValue = normalizeStatus(c.campaign_status ?? c.status);
    const statusMatch = !hasStatusSelection || statuses.includes(statusValue);

    if (filterOperator.value === 'and') {
      return labelMatch && inboxMatch && statusMatch;
    }
    // OR semantics over only the active filters
    const activeMatches = [];
    if (hasLabelSelection) activeMatches.push(labelMatch);
    if (hasInboxSelection) activeMatches.push(inboxMatch);
    if (hasStatusSelection) activeMatches.push(statusMatch);
    return activeMatches.length === 0 ? true : activeMatches.some(Boolean);
  });
});
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.SMS.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.SMS.NEW_CAMPAIGN')"
    @click="toggleSMSCampaignDialog()"
    @close="toggleSMSCampaignDialog(false)"
  >
    <CampaignsToolbar
      v-if="!hasNoSMSCampaigns"
      v-model="selectedLabels"
      v-model:inboxes="selectedInboxes"
      v-model:statuses="selectedStatuses"
      v-model:operator="filterOperator"
      :search-value="searchValue"
      @search="val => (searchValue = val)"
    />
    <template #left-action>
      <Button
        icon="i-lucide-rotate-cw"
        size="sm"
        :is-loading="isFetchingCampaigns"
        :disabled="isFetchingCampaigns"
        @click="refreshCampaigns"
      >
        <span class="hidden sm:inline">{{ t('CAMPAIGN.REFRESH') }}</span>
      </Button>
    </template>
    <template #action>
      <SMSCampaignDialog
        v-if="showSMSCampaignDialog"
        @close="toggleSMSCampaignDialog(false)"
      />
    </template>
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoSMSCampaigns && filteredSMSCampaigns.length"
      :campaigns="filteredSMSCampaigns"
      @delete="handleDelete"
    />
    <div
      v-else-if="!hasNoSMSCampaigns && !filteredSMSCampaigns.length"
      class="flex items-center justify-center py-10"
    >
      <span class="text-base text-n-slate-11">
        {{ $t('CAMPAIGNS.LIST.NO_RESULTS') }}
      </span>
    </div>
    <SMSCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.SMS.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.SMS.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
